import * as THREE from "three";

class ResourceTracker {
  constructor() {
    this.resources = new Set();
  }

  track(resource) {
    if (!resource) {
      return resource;
    }

    // handle children and when material is an array of materials or uniform is array of textures
    if (Array.isArray(resource)) {
      resource.forEach(element => this.track(element));
      return resource;
    }

    if (resource.dispose || resource instanceof THREE.Object3D) {
      this.resources.add(resource);
    }

    if (resource instanceof THREE.Object3D) {
      this.track(resource.geometry);
      this.track(resource.material);
      this.track(resource.children);
    } else if (resource instanceof THREE.Material) {
      // We have to check if there are any textures on the material
      for (const value of Object.values(resource)) {
        if (value instanceof THREE.Texture) {
          this.track(value);
        }
      }
      // We also have to check if any uniforms reference textures or arrays of textures
      if (resource.uniforms) {
        for (const value of Object.values(resource.uniforms)) {
          if (value) {
            const uniformValue = value.value;

            if (uniformValue instanceof THREE.Texture || Array.isArray(uniformValue)) {
              this.track(uniformValue);
            }
          }
        }
      }
    } else if (resource instanceof THREE.WebGLRenderer && resource.renderLists) {
      this.track(resource.renderLists);
    }

    return resource;
  }

  disposeByResource(resource) {
    if (!resource) {
      return;
    }

    if (Array.isArray(resource)) {
      resource.forEach(element => this.disposeByResource(element));
      return;
    }

    if (resource instanceof THREE.Object3D) {
      this.disposeByResource(resource.geometry);
      this.disposeByResource(resource.material);
      this.disposeByResource(resource.children);

      if (resource.parent) {
        resource.parent.remove(resource);
      }
    } else if (resource instanceof THREE.Material) {
      // We have to check if there are any textures on the material
      for (const value of Object.values(resource)) {
        if (value instanceof THREE.Texture) {
          this.disposeByResource(value);
        }
      }
      // We also have to check if any uniforms reference textures or arrays of textures
      if (resource.uniforms) {
        for (const value of Object.values(resource.uniforms)) {
          if (value) {
            const uniformValue = value.value;

            if (uniformValue instanceof THREE.Texture || Array.isArray(uniformValue)) {
              this.disposeByResource(uniformValue);
            }
          }
        }
      }
    } else if (resource instanceof THREE.WebGLRenderer && resource.renderLists) {
      this.disposeByResource(resource.renderLists);
    }

    if (resource.dispose) {
      resource.dispose();
    }

    this.resources.delete(resource);
  }

  dispose() {
    for (const resource of this.resources) {
      if (resource instanceof THREE.Object3D && resource.parent) {
        resource.parent.remove(resource);
      }
      if (resource.dispose) {
        resource.dispose();
      }
    }
    this.resources.clear();
  }
}

export default ResourceTracker;
